import { memo } from 'react'

import { pctToView, type Pt } from '@/geo/types'
import type { Facing } from '@/schema'

/**
 * An anchor: the icon-in-a-circle at `at`, the small foot ring at `stand`,
 * the line joining them and an arrow for `facing` (§3.2, §4.3.1).
 *
 * The icon is a letter rather than a lucide glyph because an SVG canvas
 * cannot mount a React icon cheaply at forty markers; the letter is the
 * kind's initial and the `<title>` carries the full name for screen readers
 * and for hover.
 */

export interface AnchorMarkerProps {
  id: string
  kind: string
  label?: string
  at: Pt
  stand?: Pt
  facing?: Facing
  colour: string
  /** Circle radius in percent of width, already divided by the zoom. */
  radius: number
  selected?: boolean
  hovered?: boolean
  locked?: boolean
  ghost?: boolean
  onPointerDown?: (ev: React.PointerEvent<SVGElement>) => void
}

const FACING_ANGLE: Record<Facing, number> = {
  right: 0,
  '3q_right': 45,
  camera: 90,
  '3q_left': 135,
  left: 180,
  away: 270,
}

export const AnchorMarker = memo(function AnchorMarker({
  id,
  kind,
  label,
  at,
  stand,
  facing,
  colour,
  radius,
  selected,
  hovered,
  locked,
  ghost,
  onPointerDown,
}: AnchorMarkerProps) {
  const [x, y] = pctToView(at)
  const r = Math.max(0.5, radius * (selected || hovered ? 1.25 : 1))
  const standPt = stand ? pctToView(stand) : null
  const angle = facing ? FACING_ANGLE[facing] : null

  return (
    <g
      data-id={id}
      role="img"
      aria-label={`${label ?? kind}${locked ? ', locked' : ''}`}
      style={{ pointerEvents: ghost ? 'none' : 'auto', cursor: 'pointer' }}
      onPointerDown={onPointerDown}
      opacity={ghost ? 0.7 : 1}
    >
      <title>{label ? `${label} (${kind})` : kind}</title>

      {standPt ? (
        <>
          <line
            x1={x}
            y1={y}
            x2={standPt[0]}
            y2={standPt[1]}
            stroke={colour}
            strokeWidth={r * 0.22}
            strokeDasharray={`${r * 0.5} ${r * 0.4}`}
            vectorEffect="non-scaling-stroke"
          />
          <circle cx={standPt[0]} cy={standPt[1]} r={r * 0.5} fill="none" stroke={colour} strokeWidth={r * 0.26} vectorEffect="non-scaling-stroke" />
        </>
      ) : null}

      <circle
        cx={x}
        cy={y}
        r={r}
        fill={colour}
        fillOpacity={ghost ? 0.35 : 0.9}
        stroke={selected ? 'white' : colour}
        strokeWidth={r * 0.24}
        strokeDasharray={ghost ? `${r * 0.5} ${r * 0.4}` : undefined}
        vectorEffect="non-scaling-stroke"
      />
      <text x={x} y={y + r * 0.42} textAnchor="middle" fontSize={r * 1.2} fontWeight={700} fill="white" pointerEvents="none">
        {initialOf(kind)}
      </text>

      {angle !== null ? (
        <path
          d={`M${x + r * 1.25},${y} l${-r * 0.55},${-r * 0.42} l0,${r * 0.84} Z`}
          transform={`rotate(${angle} ${x} ${y})`}
          fill={colour}
          pointerEvents="none"
        />
      ) : null}

      {locked ? <circle cx={x + r * 0.9} cy={y - r * 0.9} r={r * 0.32} fill="white" stroke={colour} strokeWidth={r * 0.16} pointerEvents="none" /> : null}
    </g>
  )
})

function initialOf(kind: string): string {
  const clean = kind.replace(/^x:/, '')
  return (clean[0] ?? '?').toUpperCase()
}
